import { CaretLeft } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCurrentWorkspace } from "~/api/queries";
import { NotFoundScene } from "~/features/NotFoundScene";
import {
  findSettingsSection,
  type SettingsGroup,
  visibleSettingsGroups,
} from "~/features/settings/registry";
import { Scrollable } from "~/foundation/components/Scrollable";
import { Text } from "~/foundation/components/Text";
import { useDocumentTitle } from "~/foundation/hooks/useDocumentTitle";
import { cn } from "~/foundation/utils/cn";
import { NavSearchInput, PinToggle } from "~/shell/NavPrimitives";
import { settingsDestinationId } from "~/shell/navCatalog";

type Translate = (key: string) => string;

/** Title, group title or keywords — on the displayed language, like the shell's search. */
function filterGroups(groups: SettingsGroup[], query: string, t: Translate): SettingsGroup[] {
  const q = query.trim().toLocaleLowerCase();
  if (!q) {
    return groups;
  }
  return groups
    .map((group) => ({
      ...group,
      sections: group.sections.filter(
        (section) =>
          t(section.title).toLocaleLowerCase().includes(q) ||
          t(group.title).toLocaleLowerCase().includes(q) ||
          (section.keywords ?? "").toLocaleLowerCase().includes(q),
      ),
    }))
    .filter((group) => group.sections.length > 0);
}

/**
 * The settings layer of the nav (layered nav, 2026-09-09): "‹ Back to workspace" on top,
 * a filter over the 28 sections, and a pin star on every link — the same primitives as the
 * shell's sidebar and product picker, so the two never drift.
 */
function SettingsNav({ activeId }: { activeId: string }) {
  const { t } = useTranslation();
  const { data: workspace } = useCurrentWorkspace();
  const [query, setQuery] = useState("");
  const groups = filterGroups(visibleSettingsGroups(workspace?.abilities ?? {}), query, t);

  return (
    // flex-col + min-h-0 is what lets the 28-section list scroll instead of overflowing.
    <nav
      aria-label={t("Settings")}
      className="flex h-full w-56 min-h-0 shrink-0 flex-col border-e border-divider"
    >
      <div className="flex shrink-0 flex-col gap-1 px-3 pt-2.5 pb-2">
        <Link
          to="/"
          className="-ms-1 inline-flex h-6 items-center gap-1 self-start rounded-sm ps-1 pe-2 text-[12px] font-medium text-text-tertiary hover:bg-hover-bg hover:text-text"
        >
          <CaretLeft size={12} aria-hidden className="rtl:rotate-180" />
          {t("Back to workspace")}
        </Link>
        <p className="min-h-7 truncate pb-1 text-body font-semibold text-text">{t("Settings")}</p>
        <NavSearchInput
          compact
          value={query}
          onChange={setQuery}
          placeholder={t("Find a setting…")}
        />
      </div>
      <Scrollable className="px-2 pb-4">
        {groups.length === 0 ? (
          <p role="status" className="px-2 py-4 text-small text-text-tertiary">
            {t("No settings match “{{query}}”", { query: query.trim() })}
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {groups.map((group) => (
              <div key={group.id} className="flex flex-col gap-0.5">
                <p className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-text-tertiary">
                  {t(group.title)}
                </p>
                {group.sections.map((section) => {
                  const Icon = section.icon;
                  const active = section.id === activeId;
                  return (
                    <div key={section.id} className="group relative flex items-center">
                      <Link
                        to="/settings/$section"
                        params={{ section: section.id }}
                        aria-current={active ? "page" : undefined}
                        className={cn(
                          "flex h-8 min-w-0 flex-1 items-center gap-2.5 rounded-sm px-2 pe-8 text-body font-medium",
                          "transition-colors duration-100 ease-in-out motion-reduce:transition-none",
                          active
                            ? "bg-sidebar-active-bg text-text"
                            : "text-text-secondary hover:bg-hover-bg",
                        )}
                      >
                        <Icon size={16} aria-hidden className="shrink-0" />
                        <span className="truncate">{t(section.title)}</span>
                      </Link>
                      <span className="absolute end-1 top-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-100 group-hover:opacity-100 group-focus-within:opacity-100 has-[[data-pinned=true]]:opacity-100 motion-reduce:transition-none">
                        <PinToggle id={settingsDestinationId(section.id)} />
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </Scrollable>
    </nav>
  );
}

/**
 * One scene renders all 28 settings sections: the registry supplies the body, the gate and
 * the copy. Adding a section is a registry entry, not a route.
 */
export function SettingsScene({ sectionId }: { sectionId: string }) {
  const { t } = useTranslation();
  const { data: workspace } = useCurrentWorkspace();
  const section = findSettingsSection(sectionId);
  const abilities = workspace?.abilities ?? {};
  const permitted = section ? (section.visible ? section.visible(abilities) : true) : false;

  useDocumentTitle(section ? `${t(section.title)} · ${t("Settings")}` : t("Settings"));

  if (!section || !permitted) {
    // An unknown slug and a slug the viewer may not open are the same answer on purpose:
    // a 404 does not reveal that a section exists.
    return <NotFoundScene />;
  }

  const Body = section.component;

  return (
    <div className="flex h-full">
      <div className="hidden md:block">
        <SettingsNav activeId={section.id} />
      </div>

      {/* min-w-0: the page never grows past the viewport to fit its widest row (a phone clipped Thinkers → Usage). */}
      <div className="flex h-full min-w-0 flex-1 flex-col">
        <Scrollable className="flex-1">
          <div
            className={cn(
              "mx-auto flex w-full flex-col gap-6 p-6",
              section.wide ? "max-w-4xl" : "max-w-2xl",
            )}
          >
            <div>
              <Text as="h1" variant="h2">
                {t(section.title)}
              </Text>
              <Text variant="secondary" className="mt-1">
                {t(section.description)}
              </Text>
            </div>
            <Body />
          </div>
        </Scrollable>
      </div>
    </div>
  );
}
